import { pipe, Runtime, ReadonlyArray } from "fp";

import * as Data from "~/Data";
import * as DriverStatus from "~/Driver/Status";
import * as Job from "~/Job";
import { Time } from "~/Time";
import * as WorkCode from "~/WorkCode";
import { WorkOrder } from "~/WorkOrder";

export * as Action from "./Action";
export * as Request from "./Request";

export type Entry = Data.Reference.Reference &
  Time.Interval.Stopped &
  Partial<WorkOrder> & {
    readonly workCodes?: readonly WorkCode.WorkCode[];
    readonly job?: Data.Reference.Reference & Job.Job;
  };

/**
 * Overlap no longer than this is a boundary artifact rather than shared time.
 *
 * Duty periods come from the ELD event stream; time entries come from the
 * time-tracking service. The two record the same duty change independently, so
 * their timestamps for it can differ by around a second — enough that the entry
 * opening at a transition reaches a moment back into the period that closes
 * there. The two intervals then "overlap", but only across the transition
 * itself, and the work belongs wholly to the period the entry opens.
 *
 * Calibrated to the one-second skew seen on staging. A larger skew would need
 * this raised, which is why it is a named constant and not an inline `1000`.
 *
 * A function, not a value: `~/Time` and `~/TimeTracking` import each other, so
 * `Time.Duration` is still undefined while this module is being set up, and
 * evaluating it at module scope breaks every importer of `~/Time` at load.
 * Same family as the `import X = A.B.C` note in CLAUDE.md.
 */
const boundaryArtifact = () => Time.Duration.seconds(1);

type Bounds = Time.Interval.Stopped["interval"];

/** Milliseconds two intervals share. Zero when they only touch. */
const shared = (a: Bounds, b: Bounds): number =>
  Math.max(
    0,
    Math.min(a.stop.dateTime, b.stop.dateTime) -
      Math.max(a.start.dateTime, b.start.dateTime)
  );

/**
 * Is `inner` wholly inside `outer`?
 *
 * Half-open at the closing edge, so an instant sitting exactly on a shared
 * boundary is inside the interval it opens and not the one it closes.
 */
const within = (outer: Bounds) => (inner: Bounds): boolean =>
  inner.start.dateTime >= outer.start.dateTime &&
  inner.stop.dateTime <= outer.stop.dateTime &&
  inner.start.dateTime < outer.stop.dateTime;

/** Milliseconds `entry` and `period` share. Zero when they only touch. */
export const overlapWith = ({ interval: period }: Time.Interval.Stopped) => ({
  interval: entry,
}: Entry): number => shared(period, entry);

/**
 * Should `entry`'s work be attributed to `period`?
 *
 * Deliberately not `Time.Interval.Stopped.collides`, which is built on an
 * inclusive `contains` and so treats merely adjacent intervals as colliding.
 * That may well be what its other consumers want, but here it is wrong: duty
 * periods and the time entries generated from them are both contiguous chains
 * sharing exact boundary instants, so an inclusive test matches every period
 * against the entry that *ends* where the period begins.
 *
 * Either one **containing** the other is attributed, whatever the lengths
 * involved — neither can belong anywhere else. The threshold applies only when
 * they genuinely **cross** a boundary, where they must share more than a
 * `boundaryArtifact`'s worth of time; that is what keeps clock skew between the
 * two systems from bleeding one period's work onto the next.
 *
 * The containment check has to run in both directions. Requiring it only of the
 * entry silently discarded the sole entry covering a duty period shorter than
 * the threshold — a rapid or duplicated status flip — because the shared time
 * is then the whole period and still under the limit. That is the opposite
 * failure to the one the threshold exists to prevent.
 *
 * Note none of this is covered by picking the greatest overlap afterwards: a
 * sliver only loses to a better candidate, and a period with no entry of its
 * own — an off-duty stretch, when the driver is clocked out — has no better
 * candidate.
 */
export const overlaps = ({ interval: period }: Time.Interval.Stopped) => ({
  interval: entry,
}: Entry): boolean =>
  within(period)(entry) ||
  within(entry)(period) ||
  shared(period, entry) > boundaryArtifact().duration;


export const useStore = (): Data.Store.Store<Entry> =>
  Data.Store.useSelector(({ timeTracking: { entries } }) => entries.store);

/**
 * A duty period, and the status that decides whether it can carry work.
 *
 * The status is part of the question, not an optional extra: "which entry
 * overlaps this interval" is not the same question as "whose work belongs on
 * this row", and only the second one has a useful answer here.
 */
export type Period = Time.Interval.Stopped & {
  readonly status: DriverStatus.Status;
};

/**
 * The entry whose work belongs on `period`, if any.
 *
 * Two independent conditions, and both live here rather than at the call sites:
 *
 * - The period must be **on duty**. Off duty means not working, so however the
 *   intervals line up the row stays blank.
 * - Among the entries that overlap it, the one sharing the **greatest**
 *   duration wins. Taking the first match made the answer depend on the store's
 *   key order rather than on the data.
 *
 * The duty check used to sit in each consumer, which is exactly how
 * `Log/Revision/Form` kept rendering work orders against off-duty revisions
 * after the table and the CSV export had both been fixed. Requiring a `Period`
 * rather than a bare interval means a caller cannot reach this without having
 * answered the question.
 */
export const fromInterval = (period: Period) => (
  store: Data.Store.Store<Entry>
): Entry | undefined =>
  !DriverStatus.isOnDuty(period.status)
    ? undefined
    : pipe(
        store,
        Data.Store.toCache,
        Data.Cache.toList((_id, entry: Entry) => entry),
        ReadonlyArray.filter(overlaps(period)),
        ReadonlyArray.reduce<Entry, Entry | undefined>(
          undefined,
          (best, entry) =>
            !best || overlapWith(period)(entry) > overlapWith(period)(best)
              ? entry
              : best
        )
      );

export const useFromInterval = (period?: Period): Entry | undefined =>
  pipe(useStore(), !period ? () => undefined : fromInterval(period));

export namespace State {
  export type State = { readonly store: Data.Store.Store<Entry> };

  export const initial = (): State => ({ store: Data.Store.initial() });
}

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      entry_id: Runtime.string(),
      start_date: Time.DateTime.Response.runtime(),
      end_date: Time.DateTime.Response.runtime(),
      work_codes: Runtime.array(WorkCode.Response.runtime()),
      work_order: Runtime.nullable(
        Runtime.strict({
          id: Runtime.number(),
          description: Runtime.nullable(Runtime.string()),
        })
      ),

      job: Runtime.nullable(
        Runtime.strict({
          job_id: Runtime.number(),
          name: Runtime.nullable(Runtime.string()),
        })
      ),
    });

  export const decode = (response: Runtime.ToType<typeof runtime>): Entry => ({
    id: response.entry_id,
    interval: { start: response.start_date, stop: response.end_date },
    workCodes: pipe(
      response.work_codes,
      ReadonlyArray.map(WorkCode.Response.decode)
    ),

    workOrder: !response.work_order
      ? undefined
      : {
          id: `${response.work_order.id}`,
          description: response.work_order.description ?? undefined,
        },

    job: !response.job
      ? undefined
      : {
          id: `${response.job.job_id}`,
          name: response.job.name ?? undefined,
        },
  });
}
